<template>
  <svg :width="width" :height="height" class="graph">
    <g :transform="translateContent">
      <slot name="x-axis"
        v-bind="sharedProps"
        :padding="chartDefaults.xLabelPadding"
        :fontSize="chartDefaults.fontSize"
        :dataWidth="chartDefaults.dataWidth"
      />
      <slot name="y-axis"
        v-bind="sharedProps"
        :primaryKey="primaryKey"
        :secondaryKey="secondaryKey"
        :padding="chartDefaults.yLabelPadding"
        :fontSize="chartDefaults.fontSize"
        :marginTop="chartDefaults.margin.top"
      />
      <slot name="bars"
        v-bind="sharedProps"
        :primaryKey="primaryKey"
        :secondaryKey="secondaryKey"
        :marginTop="chartDefaults.margin.top"
      />
      <slot name="pie-chart"
        :data="data"
        :radius="radius"
      />
      <slot name="line-chart"
        v-bind="sharedProps"
        :marginTop="chartDefaults.margin.top"
        :marginLeft="chartDefaults.dataWidth/2"
      />
      <slot name="choropleth"
        :data="data"
        :width="width"
        :height="height"
        :primaryKey="primaryKey"
        :dimensionKey="dimensionKey"
      />
    </g>
    <slot name="brush"/>
  </svg>
</template>
<script>
import {
  getInterval,
  getChartMax,
} from '../../../../../../services/dashboard/graphs/intervalsService';
import { max } from 'd3-array';

export default {
  props: {
    data: {
      type: Array,
      required: true,
    },
    width: {
      type: Number,
      default: 624,
    },
    height: {
      type: Number,
      default: 271,
    },
    radius: {
      type: Number,
      default: 0,
    },
    primaryKey: {
      type: String,
    },
    secondaryKey: {
      type: String,
    },
    chartKeys: {
      type: Array,
    },
    dimensionKey: {
      type: String,
      default: 'dimension',
    },
    dualYAxis: {
      type: Boolean,
      default: false,
    },
    pieChart: {
      type: Boolean,
      default: false,
    },
    chartDefaults: {
      type: Object,
      default: () => {
        return {
          dataWidth: 0,
          xLabelPadding: 0,
          yLabelPadding: 0,
          yLabelWidth: 0,
          fontSize: 0,
          margin: {
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
          },
        };
      },
    },
  },
  computed: {
    sharedProps() {
      return {
        data: this.data,
        defaultChartMax: this.chartMax,
        defaultInterval: this.interval,
        dimensionKey: this.dimensionKey,
        contentDimensions: this.contentDimensions,
      };
    },
    contentDimensions() {
      return {
        marginLeft:
          this.chartDefaults.margin.left
            + this.chartDefaults.yLabelWidth
            + this.chartDefaults.yLabelPadding
            + this.radius,
        marginTop:
          this.chartDefaults.margin.top
            + this.radius,
        width:
          this.width - (this.dualYAxis ? 2 : 1) * (
            this.chartDefaults.margin.left
            + this.chartDefaults.margin.right
            + this.chartDefaults.yLabelWidth
            + this.chartDefaults.yLabelPadding
          ),
        height:
          this.height
            - this.chartDefaults.margin.top
            - this.chartDefaults.fontSize
            - this.chartDefaults.xLabelPadding,
      };
    },
    translateContent() {
      if (this.pieChart) {
        return `translate(${this.contentDimensions.width / 2}, ` +
          `${this.contentDimensions.height / 2})`;
      }

      return `translate(${this.contentDimensions.marginLeft}, ${this.contentDimensions.marginTop})`;
    },
    dataMax() {
      const emptyChartMax = 11;

      if (this.chartKeys) {
        return max(this.data, (d) => {
          const values = this.chartKeys.map(k => d[k.value] || 0);

          return Math.max(...values);
        }) || emptyChartMax;
      }

      return max(this.data, d => d[this.primaryKey]) || emptyChartMax;
    },
    interval() {
      return getInterval(this.dataMax);
    },
    chartMax() {
      return getChartMax(this.dataMax, this.interval);
    },
  },
};
</script>
<style scoped>
.graph {
  font-family: Lato;
  font-weight: 500;
}
</style>
